import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { toast } from "react-toastify";
import {
  confirmPartnerDocumentRequest,
  removePartnerDocumentFile,
  shareDocumentRequestWithClient,
  uploadPartnerDocumentFile,
} from "../../api/endpoints/documents";
import { StatusChip } from "../../components/ui";
import {
  canConfirmRequest,
  fileSourceLabel,
  isRemovedByOffice,
  isReuploadRequest,
  itemStateLabel,
  itemStateToneOverride,
  progressSummary,
  requestProgress,
} from "./documentHelpers";

const ITEM_CHIP_TONES = { default: "amber", success: "emerald", error: "rose", neutral: "neutral" };
const SOURCE_CHIP_TONES = { CLIENT: "sky", YOU: "neutral", OFFICE: "neutral" };

/** What the partner should do next, from where the request stands. */
const nextStep = (request, progress, missingRequired, isReupload) => {
  if (request.status === "SUBMITTED") {
    if (isReupload) {
      return {
        tone: "done",
        title: "Reuploaded — new copies sent to the office",
        body: "This round is done. The replacement files are with the office for review.",
      };
    }
    return {
      tone: "done",
      title: "Sent to the office",
      body: "The office has these documents. Nothing more to do here.",
    };
  }
  if (request.status === "REVOKED") {
    return { tone: "muted", title: "Cancelled", body: "The office cancelled this request." };
  }
  if (progress.required > 0 && missingRequired === 0) {
    return {
      tone: "ready",
      title: "All required documents are in",
      body: "Check the files below, then send them to the office.",
    };
  }
  return {
    tone: "todo",
    title: `${missingRequired} required document${missingRequired === 1 ? "" : "s"} still missing`,
    body: "Upload them yourself, or send the client the secure link to upload them.",
  };
};

const STEP_STYLES = {
  todo: "border-amber-200 bg-amber-50 text-amber-900",
  ready: "border-emerald-200 bg-emerald-50 text-emerald-900",
  done: "border-emerald-200 bg-emerald-50 text-emerald-900",
  muted: "border-slate-200 bg-slate-50 text-slate-700",
};

const errorMessage = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

/**
 * Everything a partner does on one document request: upload a file against each named document,
 * email the client the secure upload link, and send the finished set to the office. Used on the
 * request's own page and inline on the client's Documents tab.
 */
const DocumentRequestWorkspace = ({ request, onChanged }) => {
  const requestId = request.id;
  const [uploadingItem, setUploadingItem] = useState(null);
  const [removeAsk, setRemoveAsk] = useState(null);
  const [shareAsk, setShareAsk] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [confirmAsk, setConfirmAsk] = useState(false);

  const uploadMutation = useMutation(
    ({ itemId, file }) => uploadPartnerDocumentFile(requestId, itemId, file),
    {
      onSuccess: () => {
        toast.success("File uploaded");
        onChanged();
      },
      onError: (err) => toast.error(errorMessage(err, "Upload failed")),
      onSettled: () => setUploadingItem(null),
    }
  );

  const removeMutation = useMutation((file) => removePartnerDocumentFile(requestId, file.id), {
    onSuccess: () => toast.success("File removed"),
    onError: (err) => toast.error(errorMessage(err, "Could not remove the file")),
    onSettled: () => {
      setRemoveAsk(null);
      onChanged();
    },
  });

  const shareMutation = useMutation(() => shareDocumentRequestWithClient(requestId), {
    onSuccess: (url) => {
      setShareAsk(false);
      setShareResult(url);
      onChanged();
    },
    onError: (err) => toast.error(errorMessage(err, "Could not create the link")),
  });

  const confirmMutation = useMutation(() => confirmPartnerDocumentRequest(requestId), {
    onSuccess: () => {
      toast.success("Sent to the office");
      setConfirmAsk(false);
      onChanged();
    },
    onError: (err) => toast.error(errorMessage(err, "Could not send")),
  });

  const progress = requestProgress(request);
  const confirmable = canConfirmRequest(request);
  const open = request.status === "ACTIVE";
  const items = request.items || [];
  const isReupload = isReuploadRequest(request);

  const pickFile = (itemId, file) => {
    if (!file) return;
    setUploadingItem(itemId);
    uploadMutation.mutate({ itemId, file });
  };

  const missingRequired = items.filter(
    (item) => item.required && item.state !== "FULFILLED"
  ).length;
  const step = nextStep(request, progress, missingRequired, isReupload);

  return (
    <div className="space-y-4">
      {isReupload && open && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="m-0 text-sm font-semibold text-rose-800">
            The office asked for {items.length === 1 ? "this document" : "these documents"} again
          </p>
          <p className="m-0 mt-0.5 text-xs text-rose-700">
            Collect a new copy from the client or upload it yourself below, then send to the office
            like a new request. Each item names the reason.
          </p>
        </div>
      )}
      <div className={`rounded-xl border px-4 py-3 ${STEP_STYLES[step.tone]}`}>
        <div className="flex items-start gap-2.5">
          {step.tone === "ready" || step.tone === "done" ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 20 }} className="mt-0.5 shrink-0" />
          ) : (
            <InfoOutlinedIcon sx={{ fontSize: 20 }} className="mt-0.5 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <p className="m-0 text-sm font-semibold">{step.title}</p>
            <p className="m-0 mt-0.5 text-xs opacity-90">{step.body}</p>
            {progress.required ? (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-white/70">
                  <div
                    className="h-full bg-current transition-all"
                    style={{ width: `${progress.receivedPercent}%` }}
                  />
                  <div
                    className="h-full bg-amber-500/80 transition-all"
                    style={{ width: `${progress.removedPercent}%` }}
                    title={
                      progress.removed > 0 ? `${progress.removed} removed by the office` : undefined
                    }
                  />
                </div>
                <span className="whitespace-nowrap text-xs font-medium tabular-nums">
                  {progressSummary(progress, open)}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          <span>Documents ({items.length})</span>
          <span>Status</span>
        </div>
        <ul className="m-0 list-none divide-y divide-slate-100 p-0">
          {items.map((item) => {
            const files = item.files || [];
            const uploading = uploadingItem === item.id;
            return (
              <li key={item.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="m-0 text-sm font-semibold text-slate-900">
                      {item.label}
                      {item.required ? (
                        <span className="text-rose-500"> *</span>
                      ) : (
                        <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
                      )}
                    </p>
                    {item.instructions ? (
                      <p className="m-0 mt-0.5 text-xs text-slate-500">{item.instructions}</p>
                    ) : null}
                  </div>
                  <StatusChip
                    tone={ITEM_CHIP_TONES[itemStateToneOverride(item, open)] || "neutral"}
                  >
                    {itemStateLabel(item, open)}
                  </StatusChip>
                </div>
                {open && (item.flagReason || item.sourceItemId) && (
                  <p className="m-0 mt-1 rounded-lg bg-rose-50 px-2.5 py-1.5 text-xs text-rose-800">
                    <span className="font-semibold">Office asked for a new copy</span>
                    {item.flagReason ? ` — ${item.flagReason}` : ""}. Upload the corrected file
                    below or send the client the secure link.
                  </p>
                )}
                {item.hasReplacedFiles && (
                  <p className="m-0 mt-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs text-slate-600">
                    <span className="font-semibold">Replaced</span> — the new copy is shown below.
                  </p>
                )}
                {isRemovedByOffice(item) && !item.flagReason && !item.sourceItemId && (
                  <p className="m-0 mt-1 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                    <span className="font-semibold">Removed by the office</span> — the previous file
                    was deleted.
                    {open ? " Upload a new copy or ask the client for one." : ""}
                  </p>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {files.map((file) => (
                    <span
                      key={file.id}
                      className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 py-1 pl-2 pr-1 text-xs text-slate-700"
                    >
                      <InsertDriveFileOutlinedIcon
                        sx={{ fontSize: 14 }}
                        className="text-slate-400"
                      />
                      <span className="max-w-[12rem] truncate">{file.originalFilename}</span>
                      {fileSourceLabel(file) ? (
                        <StatusChip tone={SOURCE_CHIP_TONES[file.uploadedBy] || "neutral"}>
                          {fileSourceLabel(file)}
                        </StatusChip>
                      ) : null}
                      {file.url ? (
                        <Tooltip title="Open">
                          <IconButton
                            size="small"
                            component="a"
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                            sx={{ p: 0.25 }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {file.removable ? (
                        <Tooltip title="Remove">
                          <span>
                            <IconButton
                              size="small"
                              disabled={removeMutation.isLoading}
                              onClick={() => setRemoveAsk(file)}
                              sx={{ p: 0.25 }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </span>
                  ))}

                  {open ? (
                    <Button
                      component="label"
                      size="small"
                      variant={files.length ? "text" : "outlined"}
                      disabled={uploading}
                      startIcon={
                        uploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />
                      }
                    >
                      {uploading ? "Uploading…" : files.length ? "Add another" : "Upload"}
                      <input
                        type="file"
                        hidden
                        onChange={(event) => {
                          pickFile(item.id, event.target.files?.[0]);
                          event.target.value = "";
                        }}
                      />
                    </Button>
                  ) : files.length === 0 ? (
                    <span className="text-xs text-slate-400">No file</span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {open ? (
        <div className="sticky bottom-0 -mx-1 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="m-0 text-xs text-slate-500">
              {confirmable
                ? "Everything required is in. Sending stops the client's link from working."
                : "Send to office unlocks once every required document has a file."}
            </p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                variant="outlined"
                size="small"
                disabled={shareMutation.isLoading}
                onClick={() => setShareAsk(true)}
              >
                Send secure link to client
              </Button>
              <Button
                variant="contained"
                size="small"
                disableElevation
                disabled={!confirmable || confirmMutation.isLoading}
                onClick={() => setConfirmAsk(true)}
              >
                Send to office
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={shareAsk}
        title="Send the secure link to the client?"
        message="The client gets the upload link by email and verifies with a one-time code. Anything they upload shows here marked “From client”."
        confirmLabel="Send link"
        loading={shareMutation.isLoading}
        onConfirm={() => shareMutation.mutate()}
        onCancel={() => setShareAsk(false)}
      />

      <ConfirmDialog
        open={Boolean(shareResult)}
        title="Link sent to the client"
        message={shareResult || ""}
        confirmLabel="Copy link"
        onConfirm={() => {
          if (navigator?.clipboard && shareResult) navigator.clipboard.writeText(shareResult);
          setShareResult(null);
        }}
        onCancel={() => setShareResult(null)}
      />

      <ConfirmDialog
        open={Boolean(removeAsk)}
        title="Remove this file?"
        message={`"${
          removeAsk?.originalFilename || ""
        }" will be removed from this request. You can upload a different file afterwards.`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={removeMutation.isLoading}
        onConfirm={() => removeMutation.mutate(removeAsk)}
        onCancel={() => setRemoveAsk(null)}
      />

      <ConfirmDialog
        open={confirmAsk}
        title="Send these documents to the office?"
        message={
          request.targetModuleType
            ? `The collected files will appear in ${request.targetModuleType} entry ${request.targetModuleEntryId} straight away, with no re-upload.`
            : "The collected files will be sent to the office for processing."
        }
        content={
          <Alert severity="warning">
            If you sent the secure link to the client, it stops working once you send, and the
            documents go in on their behalf. Files the client already uploaded are included.
          </Alert>
        }
        confirmLabel="Send to office"
        loading={confirmMutation.isLoading}
        onConfirm={() => confirmMutation.mutate()}
        onCancel={() => setConfirmAsk(false)}
      />
    </div>
  );
};

export default DocumentRequestWorkspace;
